<template>
  <HeaderBar />

  <section class="flex flex-col gap-6 bg-hazel-50 p-6">
    <div class="flex gap-6 border-b border-obsidian-200">
      <button
        type="button"
        class="border-b-2 pb-2 text-sm font-medium transition"
        :class="
          peerView === 'receivables'
            ? 'border-obsidian-900 text-obsidian-900'
            : 'border-transparent text-obsidian-500 hover:text-obsidian-700'
        "
        @click="peerView = 'receivables'"
      >
        {{ t('receivables') }}
      </button>
      <button
        type="button"
        class="border-b-2 pb-2 text-sm font-medium transition"
        :class="
          peerView === 'settlementBatches'
            ? 'border-obsidian-900 text-obsidian-900'
            : 'border-transparent text-obsidian-500 hover:text-obsidian-700'
        "
        @click="peerView = 'settlementBatches'"
      >
        {{ t('settlementBatches') }}
      </button>
      <button
        type="button"
        class="border-b-2 pb-2 text-sm font-medium transition"
        :class="
          peerView === 'bankStatements'
            ? 'border-obsidian-900 text-obsidian-900'
            : 'border-transparent text-obsidian-500 hover:text-obsidian-700'
        "
        @click="peerView = 'bankStatements'"
      >
        {{ t('bankStatements') }}
      </button>
      <button
        type="button"
        class="border-b-2 pb-2 text-sm font-medium transition"
        :class="
          peerView === 'writeOffs'
            ? 'border-obsidian-900 text-obsidian-900'
            : 'border-transparent text-obsidian-500 hover:text-obsidian-700'
        "
        @click="peerView = 'writeOffs'"
      >
        {{ t('writeOffs') }}
      </button>
      <div class="ml-auto pb-2">
        <LegalEntityFilter />
      </div>
    </div>

    <template v-if="peerView === 'receivables'">
      <div class="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <button
          type="button"
          class="rounded-md border p-4 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-obsidian-300 focus-visible:ring-offset-2"
          :class="widgetClass(SummaryTab.DUE_NOW)"
          @click="selectedTab = SummaryTab.DUE_NOW"
        >
          <p class="text-sm" :class="widgetLabelClass(SummaryTab.DUE_NOW)">{{ t('dueNow') }}</p>
          <p
            class="mt-2 text-base font-semibold xl:text-lg"
            :class="widgetAmountClass(SummaryTab.DUE_NOW)"
          >
            {{ formatCurrency(summary?.due1hAmount ?? 0) }}
          </p>
        </button>
        <button
          type="button"
          class="rounded-md border p-4 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-obsidian-300 focus-visible:ring-offset-2"
          :class="widgetClass(SummaryTab.OVERDUE_7)"
          @click="selectedTab = SummaryTab.OVERDUE_7"
        >
          <p class="text-sm" :class="widgetLabelClass(SummaryTab.OVERDUE_7)">{{ t('overdue7') }}</p>
          <p
            class="mt-2 text-base font-semibold xl:text-lg"
            :class="widgetAmountClass(SummaryTab.OVERDUE_7)"
          >
            {{ formatCurrency(summary?.due7dAmount ?? 0) }}
          </p>
        </button>
        <button
          type="button"
          class="rounded-md border p-4 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-obsidian-300 focus-visible:ring-offset-2"
          :class="widgetClass(SummaryTab.OVERDUE_30)"
          @click="selectedTab = SummaryTab.OVERDUE_30"
        >
          <p class="text-sm" :class="widgetLabelClass(SummaryTab.OVERDUE_30)">
            {{ t('overdue30') }}
          </p>
          <p
            class="mt-2 text-base font-semibold xl:text-lg"
            :class="widgetAmountClass(SummaryTab.OVERDUE_30)"
          >
            {{ formatCurrency(summary?.due30dAmount ?? 0) }}
          </p>
        </button>
        <button
          type="button"
          class="rounded-md border p-4 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-obsidian-300 focus-visible:ring-offset-2"
          :class="widgetClass(SummaryTab.CREDITS)"
          @click="selectedTab = SummaryTab.CREDITS"
        >
          <p class="text-sm" :class="widgetLabelClass(SummaryTab.CREDITS)">{{ t('credits') }}</p>
          <p
            class="mt-2 text-base font-semibold xl:text-lg"
            :class="widgetAmountClass(SummaryTab.CREDITS)"
          >
            {{ formatCurrency(creditSummaryAmount) }}
          </p>
        </button>
        <button
          type="button"
          class="rounded-md border p-4 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-obsidian-300 focus-visible:ring-offset-2"
          :class="downpaymentsWidgetClass"
          @click="selectedTab = 'downpayments'"
        >
          <p class="text-sm" :class="downpaymentsWidgetLabelClass">{{ t('downpaymentsTab') }}</p>
          <p class="mt-2 text-base font-semibold xl:text-lg" :class="downpaymentsWidgetAmountClass">
            {{ formatCurrency(downpaymentsTotalAmount) }}
          </p>
        </button>
        <button
          type="button"
          class="rounded-md border p-4 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-obsidian-300 focus-visible:ring-offset-2"
          :class="problemsWidgetClass"
          @click="selectedTab = 'problems'"
        >
          <p class="text-sm" :class="problemsWidgetLabelClass">
            {{ t('problemsTab') }}
          </p>
          <p class="mt-2 text-lg font-semibold" :class="problemsWidgetAmountClass">
            {{ problemsCount }}
          </p>
        </button>
      </div>

      <ReceivablesFilters
        v-if="viewMode !== 'problems'"
        v-model:search="search"
        v-model:periodicFilter="periodicFilter"
        v-model:invoicedFilter="invoicedFilter"
        v-model:depositFilter="depositFilter"
        v-model:selectedInvoiceTypes="selectedInvoiceTypes"
        :invoice-filters-disabled="viewMode === 'downpayments'"
      />
      <ReceivablesList
        v-if="viewMode === 'receivables'"
        :search="search"
        :periodic-filter="periodicFilter"
        :invoiced-filter="invoicedFilter"
        :deposit-filter="depositFilter"
        :selected-invoice-types="selectedInvoiceTypes"
        :due-days="dueDays"
        :is-credit="isCreditFilter"
        :row-key="rowKey"
        :format-customer-name="formatCustomerName"
      />
      <ReceivablesProblems
        v-else-if="viewMode === 'problems'"
        :problems="problems"
        :problems-status="problemsQuery.status.value"
        :problems-error="problemsQuery.error.value"
      />
      <ReceivablesDownpayments
        v-else
        :downpayments="downpayments"
        :downpayments-status="downpaymentsQuery.status.value"
        :downpayments-error="downpaymentsQuery.error.value"
      />
    </template>

    <SettlementBatchesView v-else-if="peerView === 'settlementBatches'" />
    <BankStatementsView v-else-if="peerView === 'bankStatements'" />
    <WriteOffsView v-else />
  </section>
</template>

<script setup lang="ts">
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import LegalEntityFilter from '@/components/LegalEntityFilter.vue';
import { useLegalEntityFilter } from '@/hooks/use-legal-entity-filter';
import { formatCurrency } from '@/utils/format-numbers';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch, watchEffect } from 'vue';
import { refDebounced } from '@vueuse/core';
import { useUiStore } from '@/stores/ui.store';
import { useRouteQueryStore } from '@/stores/route-query.store';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import {
  useReceivablesDownpayments,
  useReceivablesProblems,
  useReceivablesSummary,
} from '@/queries/use-receivables';
import ReceivablesFilters from '@/views/receivables/components/ReceivablesFilters.vue';
import ReceivablesList from '@/views/receivables/components/ReceivablesList.vue';
import ReceivablesDownpayments from '@/views/receivables/components/ReceivablesDownpayments.vue';
import ReceivablesProblems from '@/views/receivables/components/ReceivablesProblems.vue';
import SettlementBatchesView from '@/views/receivables/components/SettlementBatchesView.vue';
import BankStatementsView from '@/views/receivables/components/BankStatementsView.vue';
import WriteOffsView from '@/views/receivables/components/WriteOffsView.vue';
import type {
  ReceivablesControllerGetItemsData,
  ReceivablesItemDto,
} from '@carvia/ros-client-types';
import {
  receivablesBooleanQueryParam,
  receivablesInvoiceTypesParam,
} from '@/views/receivables/receivables-query-filters';

const { t } = useI18n();
const uiStore = useUiStore();
const routeQueryStore = useRouteQueryStore();
watchEffect(() => {
  uiStore.setHeaderTitle(t('receivables'));
});

enum SummaryTab {
  DUE_NOW = 'DUE_NOW',
  OVERDUE_7 = 'OVERDUE_7',
  OVERDUE_30 = 'OVERDUE_30',
  CREDITS = 'CREDITS',
}

const dueDays = ref<number>(7);
const peerView = useRouteQueryRef<
  'receivables' | 'settlementBatches' | 'bankStatements' | 'writeOffs' | 'cashFlow'
>('view', 'receivables', 'string');

watch(
  peerView,
  (value) => {
    if (value === 'cashFlow') {
      peerView.value = 'settlementBatches';
      return;
    }
    if (
      value !== 'receivables' &&
      value !== 'settlementBatches' &&
      value !== 'bankStatements' &&
      value !== 'writeOffs'
    ) {
      peerView.value = 'receivables';
      return;
    }
    if (value !== 'settlementBatches') {
      void routeQueryStore.patch({ batch: undefined });
    }
    if (value !== 'bankStatements') {
      void routeQueryStore.patch({
        statementStatus: undefined,
        accountIban: undefined,
        bookedFrom: undefined,
        bookedTo: undefined,
      });
    }
  },
  { immediate: true },
);
const selectedTab = useRouteQueryRef<SummaryTab | 'problems' | 'downpayments'>(
  'tab',
  SummaryTab.OVERDUE_7,
  'string',
);

const viewMode = computed<'receivables' | 'problems' | 'downpayments'>(() => {
  if (selectedTab.value === 'problems') return 'problems';
  if (selectedTab.value === 'downpayments') return 'downpayments';
  return 'receivables';
});
const activeTab = computed<SummaryTab>(() =>
  selectedTab.value === 'problems' || selectedTab.value === 'downpayments'
    ? SummaryTab.OVERDUE_7
    : selectedTab.value,
);

const search = useRouteQueryRef('search', '', 'string');
const periodicFilter = useRouteQueryRef('periodicFilter', null, 'string', {
  nullQueryValue: 'null',
});
const invoicedFilter = useRouteQueryRef('invoicedFilter', null, 'string', {
  nullQueryValue: 'null',
});
const depositFilter = useRouteQueryRef('depositFilter', null, 'string', {
  nullQueryValue: 'null',
});
const invoiceTypesQuery = useRouteQueryRef('invoiceTypes', '', 'string');
const selectedInvoiceTypes = computed({
  get: () =>
    invoiceTypesQuery.value
      ? invoiceTypesQuery.value
          .split(',')
          .filter((value) => value && value.toUpperCase() !== 'NULL')
      : [],
  set: (values: string[]) => {
    invoiceTypesQuery.value = values.join(',');
  },
});

const isActive = (tab: SummaryTab) => viewMode.value === 'receivables' && activeTab.value === tab;

const widgetClass = (tab: SummaryTab) => {
  if (isActive(tab)) {
    switch (tab) {
      case SummaryTab.DUE_NOW:
        return 'border-yellow-300 bg-yellow-50';
      case SummaryTab.OVERDUE_30:
        return 'border-red-300 bg-red-50';
      case SummaryTab.CREDITS:
        return 'border-blue-300 bg-blue-50';
      default:
        return 'border-bittersweet-300 bg-bittersweet-50';
    }
  }
  return 'border-obsidian-100 bg-white hover:border-obsidian-200';
};

const widgetLabelClass = (tab: SummaryTab) =>
  isActive(tab) ? 'text-obsidian-700' : 'text-obsidian-500';

const widgetAmountClass = (tab: SummaryTab) => {
  switch (tab) {
    case SummaryTab.DUE_NOW:
      return 'text-yellow-700';
    case SummaryTab.OVERDUE_30:
      return 'text-red-800';
    case SummaryTab.CREDITS:
      return 'text-blue-700';
    default:
      return 'text-bittersweet-700';
  }
};

watchEffect(() => {
  switch (activeTab.value) {
    case SummaryTab.DUE_NOW:
      dueDays.value = 0;
      break;
    case SummaryTab.OVERDUE_30:
      dueDays.value = 30;
      break;
    case SummaryTab.CREDITS:
      dueDays.value = 0;
      break;
    default:
      dueDays.value = 7;
  }
});

const isCreditFilter = computed(() => {
  if (activeTab.value === SummaryTab.CREDITS) return true;
  return false;
});

const debouncedSearch = refDebounced(search, 400);
// The legal entity filter applies to the whole page, including problems and settlement batches.
const { legalEntityId } = useLegalEntityFilter();
const summaryQueryParams = computed(() => ({
  legalEntityId: legalEntityId.value ?? undefined,
  invoiceTypes: receivablesInvoiceTypesParam(
    invoicedFilter.value,
    selectedInvoiceTypes.value,
  ) as unknown as ReceivablesControllerGetItemsData['query']['invoiceTypes'],
  hasPeriodicInvoicing: receivablesBooleanQueryParam(periodicFilter.value),
  isDeposit: receivablesBooleanQueryParam(depositFilter.value),
  search: debouncedSearch.value || undefined,
}));

// The summary widgets only show on the receivables peer view; other views skip these queries.
const isReceivablesView = computed(() => peerView.value === 'receivables');
const summaryQuery = useReceivablesSummary(summaryQueryParams, isReceivablesView);
const problemsQuery = useReceivablesProblems(legalEntityId, isReceivablesView);
const downpaymentsQueryParams = computed(() => ({
  legalEntityId: legalEntityId.value ?? undefined,
  search: debouncedSearch.value || undefined,
  isDeposit: receivablesBooleanQueryParam(depositFilter.value),
  hasPeriodicInvoicing: receivablesBooleanQueryParam(periodicFilter.value),
}));
const downpaymentsQuery = useReceivablesDownpayments(downpaymentsQueryParams, isReceivablesView);

type ReceivablesItem = ReceivablesItemDto & { dueSince?: Date | string | null };

const summary = computed(() => summaryQuery.data.value);
const problems = computed(() => problemsQuery.data.value);
const downpayments = computed(() => downpaymentsQuery.data.value);
const downpaymentsTotalAmount = computed(() => downpayments.value?.totalAmount ?? 0);
const problemsCount = computed(() => {
  const returned = problems.value?.uninvoicedBookings?.length ?? 0;
  const unallocated = problems.value?.unallocatedPayments?.length ?? 0;
  const mismatchingInvoices = problems.value?.mismatchingBookingInvoices?.length ?? 0;
  return returned + unallocated + mismatchingInvoices;
});
const problemsHasIssues = computed(() => problemsCount.value > 0);
const problemsWidgetClass = computed(() => {
  if (viewMode.value === 'problems') {
    return problemsHasIssues.value ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50';
  }
  return 'border-obsidian-100 bg-white hover:border-obsidian-200';
});
const problemsWidgetLabelClass = computed(() =>
  problemsHasIssues.value ? 'text-red-700' : 'text-green-700',
);
const problemsWidgetAmountClass = computed(() =>
  problemsHasIssues.value ? 'text-red-800' : 'text-green-800',
);
const downpaymentsWidgetClass = computed(() => {
  if (viewMode.value === 'downpayments') {
    return 'border-teal-300 bg-teal-50';
  }
  return 'border-obsidian-100 bg-white hover:border-obsidian-200';
});
const downpaymentsWidgetLabelClass = computed(() =>
  viewMode.value === 'downpayments' ? 'text-teal-700' : 'text-obsidian-500',
);
const downpaymentsWidgetAmountClass = computed(() =>
  viewMode.value === 'downpayments' ? 'text-teal-800' : 'text-obsidian-800',
);
const creditSummaryAmount = computed(() => {
  const summaryData = summary.value;
  return summaryData?.credit1hAmount ?? 0;
});

const formatCustomerName = (item: ReceivablesItem) => {
  const invoiceCustomer = item.invoiceCustomerData ?? undefined;
  const bookingCustomer = item.bookingCustomerData ?? undefined;
  const source = (invoiceCustomer ?? bookingCustomer) as Record<string, any> | undefined;
  if (!source) return '-';
  return (
    source.company ||
    [source.firstName, source.lastName].filter(Boolean).join(' ') ||
    source.email ||
    '-'
  );
};

const rowKey = (item: ReceivablesItem) =>
  `${item.bookingId ?? 'no-booking'}-${item.invoiceId ?? 'no-invoice'}`;
</script>

<i18n lang="json">
{
  "en": {
    "receivables": "Receivables",
    "settlementBatches": "Settlement Batches",
    "bankStatements": "Bank statements",
    "writeOffs": "Write-offs",
    "dueNow": "Due now",
    "overdue7": "Overdue 7 days",
    "overdue30": "Overdue 30 days",
    "credits": "Credits",
    "downpaymentsTab": "Downpayments",
    "problemsTab": "Problems"
  },
  "de": {
    "receivables": "Forderungen",
    "settlementBatches": "Settlement-Batches",
    "bankStatements": "Kontoauszüge",
    "writeOffs": "Abschreibungen",
    "dueNow": "Fällig",
    "overdue7": "Überfällig 7 Tage",
    "overdue30": "Überfällig 30 Tage",
    "credits": "Gutschriften",
    "downpaymentsTab": "Anzahlungen",
    "problemsTab": "Probleme"
  },
  "es": {
    "receivables": "Cobros pendientes",
    "settlementBatches": "Lotes de liquidación",
    "bankStatements": "Extractos bancarios",
    "writeOffs": "Cancelaciones",
    "dueNow": "Vencen ahora",
    "overdue7": "Vencidas +7 días",
    "overdue30": "Vencidas +30 días",
    "credits": "Abonos",
    "downpaymentsTab": "Anticipos",
    "problemsTab": "Incidencias"
  }
}
</i18n>
